import _ from 'lodash';

import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Clients from './Clients';
import * as Objects from './Objects';
import * as Streams from './Streams';
import * as Values from './Values';

export let logEnable = false;

type OperandStream<ValueType> = Streams.ValueStream<ValueType, Elevated.Domain<ValueType>> & Streams.Pullable;

export function ApplyScalars<ValueType, ResultType>(
    id: string,
    resultDomain: Elevated.Domain<ResultType>,
    expr: (...operands: (undefined|ValueType)[]) => ResultType,
    ...streams: OperandStream<ValueType>[]
): Streams.ValueStream<ResultType, Elevated.Domain<ResultType>> {
    const vars = {
        operands: streams.map((streams_, index) => Clients.ScalarClient.Eager<ValueType>(`${id}.operand[${index}]`)),
        result: Clients.ScalarClient.Eager<ResultType>(`${id}.result`),
    };
    const _eval = () => {
        const args = vars.operands.map(opvar  => opvar.value());
        const statuses = vars.operands.map(opvar => opvar.status());
        const value_ = expr(...args);
        const status_ = Streams.combineStatus(...statuses);
        if(logEnable) {
            console.log(`DEBUG: ${id} expr(${args}) = ${value_}`);
            console.log(`DEBUG: ${id} Streams.combineStatus(${JSON.stringify(statuses)}) = ${JSON.stringify(status_)}`);
        }
        vars.result.set(
            value_,
            status_
        );
    }
    vars.operands.forEach(opvar => opvar.watch({
        next: value_ => _.isEqual(value_, opvar.value()) || _eval(),
        complete: () => {},
        error: () => {}
    }))
    vars.operands.forEach((operand,index) => {
        const parent = streams[index].scalar;
        parent && operand.upstream(parent);
    });
    return { scalar: vars.result.stream(resultDomain, () => Promise.all(streams.map(stream => !stream.pull ? Promise.resolve() : stream.pull()))) };
}

export function Not<ValueType>(
    id: string,    
    ...operands: OperandStream<ValueType>[]
) {
    return ApplyScalars(id, Values.TheBooleanDomain, (...operands) => operands.reduce<boolean|undefined>((accum, current) => !current, undefined), ...operands);
}

export function And<ValueType>(
    id: string,    
    ...operands: OperandStream<ValueType>[]
) {
    return ApplyScalars(id, Values.TheBooleanDomain, (...operands) => operands.reduce<boolean|undefined>((accum, current) => accum && !!current, true), ...operands);
}

export function Or<ValueType>(
    id: string,    
    ...operands: OperandStream<ValueType>[]
) {
    return ApplyScalars(id, Values.TheBooleanDomain, (...operands) => operands.reduce<boolean|undefined>((accum, current) => accum || !!current, false), ...operands);
}

export function ApplySet<ValueType, ResultType>(
    id: string,
    resultDomain: Elevated.Domain<ResultType>,
    expr: (operand: (Set<ValueType>)) => ResultType,
    stream: OperandStream<ValueType>
): Streams.ValueStream<ResultType, Elevated.Domain<ResultType>> {
    const vars = {
        operand: Clients.SetClient.Eager<ValueType>(`${id}.operand`),
        result: Clients.ScalarClient.Eager<ResultType>(`${id}.result`),
    };
    const _eval = () => {
        vars.result.set(
            expr(vars.operand.value()),
            vars.operand.status()
        );
    }
    vars.operand.watch({
        next: value_ => _.isEqual(value_, vars.operand.value()) || _eval(),
        complete: () => {},
        error: () => {}
    });
    stream.set && vars.operand.upstream(stream.set);
    return { scalar: vars.result.stream(resultDomain, () => !stream.pull ? Promise.resolve() : stream.pull()) };
}

export function ApplySequence<ValueType, ResultType>(
    id: string,
    resultDomain: Elevated.Domain<ResultType>,
    expr: (operand: Array<ValueType>) => ResultType,
    stream: OperandStream<ValueType>
): Streams.ValueStream<ResultType, Elevated.Domain<ResultType>> {
    const vars = {
        operand: Clients.SequenceClient.Eager<ValueType>(`${id}.operand`),
        result: Clients.ScalarClient.Eager<ResultType>(`${id}.result`),
    };
    const _eval = () => {
        vars.result.set(
            expr(vars.operand.value()),
            vars.operand.status()
        );
    }
    vars.operand.watch({
        next: value_ => _.isEqual(value_, vars.operand.value()) || _eval(),
        complete: () => {},
        error: () => {}
    });
    stream.sequence && vars.operand.upstream(stream.sequence);
    return { scalar: vars.result.stream(resultDomain, () => !stream.pull ? Promise.resolve() : stream.pull()) };
}

export function ApplyRelation<BindingType extends Objects.BindingType, ResultType>(
    id: string,
    resultDomain: Elevated.Domain<ResultType>,
    expr: (operand: Array<BindingType>) => ResultType,
    stream: Streams.RelationStream<BindingType>
): Streams.ValueStream<ResultType, Elevated.Domain<ResultType>> {
    const vars = {
        operand: Clients.RelationClient.Eager<BindingType>(`${id}.operand`),
        result: Clients.ScalarClient.Eager<ResultType>(`${id}.result`),
    };
    const _eval = () => {
        vars.result.set(
            expr(vars.operand.entries()),
            vars.operand.status()
        );
    }
    vars.operand.watch({
        next: value_ => _.isEqual(value_, vars.operand.entries()) || _eval(),
        complete: () => {},
        error: () => {}
    });
    vars.operand.upstream(stream);
    return { scalar: vars.result.stream(resultDomain, () => !stream.pull ? Promise.resolve() : stream.pull()) };
}
